// Client-side WebP compression pipeline.
// Produces two derivatives: a small thumbnail (for the grid) and a larger
// display image (for the lightbox). We use browser-image-compression which
// resamples and re-encodes via canvas — keeps everything in-browser so the
// original camera/RAW file never leaves the device.

import imageCompression from 'browser-image-compression';
import { IMAGE_LIMITS } from '../../shared/types';

export interface ProcessedImage {
  blob: Blob;
  width: number;
  height: number;
  size: number;
  content_type: 'image/webp';
}

export interface ProcessedDerivatives {
  thumb: ProcessedImage;
  full: ProcessedImage;
}

export class ImageTooLargeError extends Error {
  constructor() {
    super(
      `That file is larger than ${Math.round(IMAGE_LIMITS.MAX_SOURCE_BYTES / 1024 / 1024)} MB. Try a smaller file or export at a lower resolution first.`,
    );
  }
}

export class UnsupportedTypeError extends Error {
  constructor(type: string) {
    super(`That file type (${type || 'unknown'}) isn't supported. Use a JPEG, PNG, HEIC, or WebP image.`);
  }
}

export async function processImage(file: File): Promise<ProcessedDerivatives> {
  if (file.size > IMAGE_LIMITS.MAX_SOURCE_BYTES) throw new ImageTooLargeError();
  if (!IMAGE_LIMITS.ALLOWED_INPUT_TYPES.includes(file.type as typeof IMAGE_LIMITS.ALLOWED_INPUT_TYPES[number])) {
    throw new UnsupportedTypeError(file.type);
  }

  const [thumbBlob, fullBlob] = await Promise.all([
    imageCompression(file, {
      maxWidthOrHeight: IMAGE_LIMITS.THUMB_MAX_PX,
      initialQuality: IMAGE_LIMITS.THUMB_QUALITY,
      fileType: IMAGE_LIMITS.OUTPUT_TYPE,
      useWebWorker: true,
    }),
    imageCompression(file, {
      maxWidthOrHeight: IMAGE_LIMITS.FULL_MAX_PX,
      initialQuality: IMAGE_LIMITS.FULL_QUALITY,
      fileType: IMAGE_LIMITS.OUTPUT_TYPE,
      useWebWorker: true,
    }),
  ]);

  const [thumb, full] = await Promise.all([
    measure(thumbBlob),
    measure(fullBlob),
  ]);

  return { thumb, full };
}

async function measure(blob: Blob): Promise<ProcessedImage> {
  const url = URL.createObjectURL(blob);
  try {
    const dim = await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error('Failed to decode compressed image.'));
      img.src = url;
    });
    return {
      blob,
      size: blob.size,
      width: dim.width,
      height: dim.height,
      content_type: 'image/webp',
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}
